import { useState, type ComponentProps } from "react";
import { Button, Input, Modal, message } from "antd";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import { useAlertMutations } from "@/hooks/useAlertMutations";
import { useAlertValidation } from "@/hooks/useAlertValidation";
import { useAppNotification } from "@/hooks/useAppNotification";
import { useScopeParams } from "@/hooks/useScopeParams";
import { formatDateLong } from "@/utils/dateUtils";
import { capitalizeFirst } from "@/utils/textFormattingUtils";
import CreateAlertForm, {
  AlertExplanationSection,
  AlertNameInput,
  AlertStatusLabel,
  AlertTestLabel,
} from "./CreateAlertForm";
import { InsightProps } from "./insights";

const { TextArea } = Input;

type AlertExplanationItems = ComponentProps<
  typeof AlertExplanationSection
>["explanationItems"];
type InsightAlert = InsightProps["alert"];

type AddAlertModalProps = {
  insight: InsightProps | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function AddAlertModal({
  insight,
  isOpen,
  onClose,
}: AddAlertModalProps) {
  if (!insight) {
    return null;
  }

  return (
    <Modal
      centered
      width={"70%"}
      open={isOpen}
      onCancel={onClose}
      title={
        <div className="flex p-4 border-b mb-0">
          {insight.alert?.id ? "Rename alert" : "Turn into alert"}
        </div>
      }
      footer={null}
      destroyOnHidden={true}
      className="no-padding-modal alert-modal"
      styles={{
        container: { height: 750, display: "flex", flexDirection: "column" },
        body: { flex: 1, minHeight: 0, overflow: "hidden" },
      }}
      bodyProps={{
        className: "bg-[#EFF1F2] rounded-b-lg",
      }}
    >
      <AddAlertModalBody insight={insight} onClose={onClose} />
    </Modal>
  );
}

type AddAlertModalBodyProps = {
  insight: InsightProps;
  onClose: () => void;
};

function AddAlertModalBody({ insight, onClose }: AddAlertModalBodyProps) {
  const { id } = useScopeParams();
  const [messageApi, contextHolder] = message.useMessage();
  const { openSuccessNotification, contextHolder: notificationContextHolder } =
    useAppNotification();
  const [alertName, setAlertName] = useState(insight.alert?.title ?? "");
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);
  const {
    alertRule,
    isAlertRuleValid,
    validateResponse,
    alertEvaluation,
    loadingValidation,
    loadingEvaluation,
    handleRuleChange,
    resetValidation,
    validateAlertRule,
  } = useAlertValidation(id, insight.id);

  const {
    createAlert,
    isCreating: isCreatingAlert,
    isCreateError: hasCreateAlertError,
    resetCreateAlert,
    updateAlert,
    isUpdating: isUpdatingAlert,
    isUpdateError: hasUpdateAlertError,
  } = useAlertMutations();

  const hasExistingAlert = Boolean(insight.alert?.id);

  const handleValidateAlertRule = async () => {
    if (alertRule.trim() === "") {
      messageApi.error("Please enter a valid alert rule.");
      return;
    }
    const result = await validateAlertRule();
    if (result?.condition_is_valid) {
      setAlertName(alertRule);
    }
  };

  const handleCreateAlert = () => {
    const payload = {
      insight_id: insight.id,
      title: alertName,
      condition: alertRule,
      condition_is_valid: validateResponse?.condition_is_valid,
      condition_is_met: alertEvaluation?.condition_is_met,
      condition_met_detail: alertEvaluation?.condition_met_detail,
      notify_email: notifyEmail,
      notify_push: notifyPush,
    };

    resetCreateAlert();
    createAlert(payload, {
      onSuccess: () => {
        openSuccessNotification({
          title: (
            <p className="text-[#003832] text-base font-semibold">
              Alert Created
            </p>
          ),
          description: (
            <p className="text-[#2B4752] text-[14px]">
              The alert has been successfully created.
            </p>
          ),
          placement: "bottom",
          className: "insight-success-confirmation",
          duration: 2,
          icon: null,
        });
        onClose();
      },
      onError: (error) => {
        console.error("Error creating alert:", error);
      },
    });
  };

  const handleUpdateAlert = () => {
    if (alertName.trim() === "") {
      messageApi.error("Please enter an alert name.");
      return;
    }

    if (!insight.alert?.id) {
      return;
    }

    updateAlert(
      {
        alertId: insight.alert.id,
        payload: { title: alertName },
      },
      {
        onSuccess: () => {
          openSuccessNotification({
            title: (
              <p className="text-[#003832] text-base font-semibold">
                Alert Updated
              </p>
            ),
            description: (
              <p className="text-[#2B4752] text-[14px]">
                The alert name has been successfully updated.
              </p>
            ),
            placement: "bottom",
            className: "insight-success-confirmation",
            icon: null,
          });
          onClose();
        },
        onError: (error) => {
          console.error("Error updating alert:", error);
        },
      },
    );
  };

  const handleEditRule = () => {
    resetValidation();
    resetCreateAlert();
  };

  return (
    <>
      {contextHolder}
      {notificationContextHolder}
      <div className="grid h-full items-stretch gap-4 lg:grid-cols-[minmax(0,2fr)_360px]">
        <AlertModalLeftColumn insight={insight} />
        <AlertModalRightColumn
          hasExistingAlert={hasExistingAlert}
          alertRule={
            hasExistingAlert ? (insight.alert?.condition ?? "") : alertRule
          }
          alert={insight.alert}
          alertName={alertName}
          onAlertNameChange={setAlertName}
          onCancel={onClose}
          onUpdate={handleUpdateAlert}
          isUpdating={isUpdatingAlert}
          hasUpdateError={hasUpdateAlertError}
          createFormProps={{
            alertName,
            isAlertRuleValid,
            handleValidateAlertRule,
            validateResponse,
            alertEvaluation,
            loadingValidation,
            loadingEvaluation,
            loadingCreateAlert: isCreatingAlert,
            errorAlert: hasCreateAlertError,
            handleCreateAlert,
            alertRule,
            onRuleChange: handleRuleChange,
            onEditRule: handleEditRule,
            onEditAlertName: setAlertName,
            notifyEmail,
            notifyPush,
            onNotificationChange: (channel, value) => {
              if (channel === "notify_email") setNotifyEmail(value);
              else setNotifyPush(value);
            },
          }}
        />
      </div>
    </>
  );
}

function AlertModalLeftColumn({ insight }: { insight: InsightProps }) {
  const metadata = [
    { label: "Category", value: capitalizeFirst(insight.category) },
    { label: "Cadence", value: capitalizeFirst(insight.cadence) },
    {
      label: "Last Updated",
      value: formatDateLong(insight.insight_result_timestamp, "en-US"),
    },
  ];

  return (
    <div className="min-w-0 rounded-lg bg-white p-5 shadow-omni ml-4 mt-4 mb-4">
      <div className="mb-4 flex flex-wrap gap-2">
        {metadata.map((item) => (
          <div
            key={item.label}
            className="rounded-md bg-[#F5F7F8] px-3 py-2 text-xs text-[#2B4752]"
          >
            <span className="mr-1 text-[#00000073]">{item.label}:</span>
            <span className="font-medium">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2 overflow-y-auto max-h-140 hide-scrollbar">
        <section>
          <p className="mb-2 text-sm font-medium text-[#2B4752]">
            <span className="bg-[#ECFAFF] p-[2px_4px]">INSIGHT</span>
          </p>
          <h2 className="text-xl font-semibold text-[#20353E]">
            {capitalizeFirst(insight.title)}
          </h2>
        </section>
        <section>
          <div className="py-4">
            <div className="markdown text-sm text-[#2B4752]">
              <MarkdownRenderer
                content={insight.av_answer || "Not available"}
              />
            </div>
          </div>
        </section>
        <section>
          <div className="rounded-md bg-[#F5F7F8] p-4 text-sm text-[#2B4752]">
            <p className="mb-2 text-xs text-[#00000073]">
              ORIGINAL INSIGHT PROMPT
            </p>
            {insight.av_question || "Not available"}
          </div>
        </section>
      </div>
    </div>
  );
}

type AlertModalRightColumnProps = {
  hasExistingAlert: boolean;
  alertRule: string;
  alert: InsightAlert;
  alertName: string;
  onAlertNameChange: (value: string) => void;
  onCancel: () => void;
  onUpdate: () => void;
  isUpdating: boolean;
  hasUpdateError: boolean;
  createFormProps: ComponentProps<typeof CreateAlertForm>;
};

function AlertModalRightColumn({
  hasExistingAlert,
  alertRule,
  alert,
  alertName,
  onAlertNameChange,
  onCancel,
  onUpdate,
  isUpdating,
  hasUpdateError,
  createFormProps,
}: AlertModalRightColumnProps) {
  const explanationItems: AlertExplanationItems = alert
    ? [
        {
          key: "1",
          label: <AlertTestLabel isValid={alert.condition_is_valid} />,
          children: (
            <div className="text-xs text-[#2B4752]">{alert.condition}</div>
          ),
          styles: {
            header: { backgroundColor: "#fff", borderRadius: "0px" },
            body: { backgroundColor: "#fff" },
          },
          showArrow: true,
          disabled: false,
        },
        {
          key: "2",
          label: <AlertStatusLabel isMet={alert.condition_is_met} />,
          children: (
            <div className="text-xs text-[#2B4752]">
              {alert.condition_met_detail ||
                "No additional explanation was provided."}
            </div>
          ),
          styles: {
            header: { backgroundColor: "#fff", borderRadius: "0px" },
            body: { backgroundColor: "#fff" },
          },
          showArrow: true,
          disabled: false,
        },
      ]
    : [];

  return (
    <div className="flex h-full self-stretch min-h-0 flex-col bg-white p-3 overflow-hidden rounded-br-lg">
      {hasExistingAlert ? (
        <div className="flex h-full min-h-0 flex-col">
          <p className="text-xs text-[#00000073] mb-3">
            Only the alert name can be edited. To change the alert rule, delete
            this alert and create a new one.
          </p>
          <div className="flex-1 overflow-y-auto hide-scrollbar">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="block text-sm text-[#2B4752] font-medium">
                Alert me if...
              </p>
            </div>
            <div className="relative mt-1 mb-2">
              <TextArea
                value={alertRule}
                rows={5}
                disabled
                className="bg-[rgba(0, 0, 0, 0.02)]"
                style={{ resize: "none" }}
              />
            </div>
            {explanationItems.length > 0 && (
              <AlertExplanationSection
                explanationItems={explanationItems}
                loadingValidation={false}
                compact
                defaultCollapsed
              />
            )}
            <AlertNameInput
              alertName={alertName}
              onChange={onAlertNameChange}
            />
          </div>
          <div className="flex justify-between items-center mt-2 pt-4 border-t border-[#0000000f]">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={onUpdate}
              loading={isUpdating}
              disabled={!alertName.trim()}
            >
              Apply
            </Button>
          </div>
          {hasUpdateError && (
            <p className="mt-2 text-xs text-red-600 font-semibold">
              Something went wrong while updating the alert. Please try again.
            </p>
          )}
        </div>
      ) : (
        <CreateAlertForm {...createFormProps} />
      )}
    </div>
  );
}
